import { authFirebase, authFirebaseAdminCreate } from '../firebase.config';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  UserCredential,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import { Toastify } from '@lift-admin/ui/toast';
import apiClient from '@lift-admin/utils/apiClient';
import { actionLogTrack } from '@lift-admin/utils/actionLogTrack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  UserPermissions,
  PermissionModule,
  PermissionAction,
  extractPermissionsFromJWT,
  hasPermission as checkHasPermission,
  canView as checkCanView,
  canEdit as checkCanEdit,
  canManage as checkCanManage,
} from '../permissions';

interface AuthContextType {
  authUser: any;
  loginUserWithPasswordEmail: (email: string, password: string) => Promise<any>;
  signUpUserWithPasswordEmail: (
    email: string,
    password: string
  ) => Promise<any>;
  /** Creates a Firebase user without switching the current session (for admin add-user). */
  createUserAsAdmin: (email: string, password: string) => Promise<string>;
  SSOSignIn: (redirectToken: string) => void;
  loginUserWithGoogleSignIn: (email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  handleLogout: () => Promise<void>;
  loading: boolean;
  willnavigate: boolean;
  willnavigateForWaiting: boolean;
  // Permission related
  permissions: UserPermissions | null;
  isSuperAdmin: boolean;
  hasPermission: (module: PermissionModule, action: PermissionAction) => boolean;
  canView: (module: PermissionModule) => boolean;
  canEdit: (module: PermissionModule) => boolean;
  canManage: (module: PermissionModule) => boolean;
  refreshPermissions: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Provide a default value for the context
const defaultContextValue: AuthContextType = {
  authUser: null,
  loginUserWithPasswordEmail: async (email: string, password: string) => {},
  signUpUserWithPasswordEmail: async (email: string, password: string) => {
    return '';
  },
  createUserAsAdmin: async () => '',
  SSOSignIn: async (redirectToken: string) => {},
  loginUserWithGoogleSignIn: async (email: string, password: string) => {},
  forgotPassword: async (email: string) => {},
  handleLogout: async () => {},
  loading: true,
  willnavigate: false,
  willnavigateForWaiting: false,
  // Permission defaults
  permissions: null,
  isSuperAdmin: false,
  hasPermission: () => false,
  canView: () => false,
  canEdit: () => false,
  canManage: () => false,
  refreshPermissions: async () => {},
};

const AuthContext = createContext<AuthContextType>(defaultContextValue);

export const useAuthContext = () => useContext(AuthContext);

// Module-level guard for SSO sign-in. React 18 StrictMode double-invokes the
// effect that calls SSOSignIn (and remounts can re-run it), which would sign in
// — and write the "logged in" admin log — twice for the same redirect token.
// A module-level Set survives component remounts within the page load, so the
// second invocation is skipped. The token is released on failure so a genuine
// retry can proceed.
const processedSsoTokens = new Set<string>();

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // const navigate = useNavigate();
  const [willnavigate, setWillNavigate] = useState<boolean>(false);
  const [willnavigateForWaiting, setWillNavigateForWaiting] =
    useState<boolean>(false);
  const [authUser, setAuthUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Permission state: loaded from JWT (with fallback when JWT has no custom claims)
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);

  // Load permissions from JWT token (fallback used when JWT has no permissions)
  const refreshPermissions = useCallback(async () => {
    try {
      const user = authFirebase.currentUser;
      if (!user) {
        setPermissions(null);
        setIsSuperAdmin(false);
        localStorage.removeItem('user_permissions');
        localStorage.removeItem('is_superadmin');
        return;
      }

      const idToken = await user.getIdToken();
      const result = extractPermissionsFromJWT(idToken);

      if (result) {
        console.log('[Auth] Using permissions:', result.permissions);
        console.log('[Auth] Using is_superadmin:', result.isSuperAdmin);
        setPermissions(result.permissions);
        setIsSuperAdmin(result.isSuperAdmin);
        localStorage.setItem('user_permissions', JSON.stringify(result.permissions));
        localStorage.setItem('is_superadmin', JSON.stringify(result.isSuperAdmin));
      }
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      // Try to use cached permissions
      const storedPermissions = localStorage.getItem('user_permissions');
      const storedSuperAdmin = localStorage.getItem('is_superadmin');
      if (storedPermissions) setPermissions(JSON.parse(storedPermissions));
      if (storedSuperAdmin) setIsSuperAdmin(JSON.parse(storedSuperAdmin));
    }
  }, []);

  // Permission check helper functions
  const hasPermission = useCallback((module: PermissionModule, action: PermissionAction): boolean => {
    return checkHasPermission(permissions ?? undefined, module, action, isSuperAdmin).hasPermission;
  }, [permissions, isSuperAdmin]);

  const canView = useCallback((module: PermissionModule): boolean => {
    return checkCanView(permissions ?? undefined, module, isSuperAdmin);
  }, [permissions, isSuperAdmin]);

  const canEdit = useCallback((module: PermissionModule): boolean => {
    return checkCanEdit(permissions ?? undefined, module, isSuperAdmin);
  }, [permissions, isSuperAdmin]);

  const canManage = useCallback((module: PermissionModule): boolean => {
    return checkCanManage(permissions ?? undefined, module, isSuperAdmin);
  }, [permissions, isSuperAdmin]);

  const loginUserWithPasswordEmail = async (
    email: string,
    password: string
  ) => {
    return await signInWithEmailAndPassword(authFirebase, email, password);
  };

  const loginUserWithGoogleSignIn = async (email: string, password: string) => {
    try {
      const googleProvider = new GoogleAuthProvider();
      const result = await signInWithPopup(authFirebase, googleProvider);
      const googleUser: any = result.user;

      if (googleUser) {
        // Check if the user already has an email/password account
        const methods = await fetchSignInMethodsForEmail(
          authFirebase,
          googleUser.email
        );
        if (methods.includes('password')) {
          // Link the Google account with the existing email/password account
          const emailPasswordCredential = EmailAuthProvider.credential(
            email,
            password
          );
          await linkWithCredential(googleUser, emailPasswordCredential);
        }

        // setUser(googleUser);
      }
    } catch (error) {
      console.error('Error signing in with Google', error);
    }
  };
  const signUpUserWithPasswordEmail = async (
    email: string,
    password: string
  ) => {
    const userCredential = await createUserWithEmailAndPassword(
      authFirebase,
      email,
      password
    );

    const res = await sendEmailVerification(userCredential.user);
    console.log(res, 'res');
    return userCredential.user.uid;
  };

  /** Creates a Firebase user using a secondary auth instance so the current admin session is unchanged. */
  const createUserAsAdmin = async (
    email: string,
    password: string
  ): Promise<string> => {
    const userCredential = await createUserWithEmailAndPassword(
      authFirebaseAdminCreate,
      email,
      password
    );
    const uid = userCredential.user.uid;

    // Email verification is best-effort; don't block user creation if it fails
    try {
      await sendEmailVerification(userCredential.user);
    } catch (e) {
      console.warn('Email verification could not be sent:', e);
    }

    // Clean up the secondary auth session
    try {
      await signOut(authFirebaseAdminCreate);
    } catch (e) {
      console.warn('Secondary auth sign-out failed:', e);
    }

    return uid;
  };

  const forgotPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(authFirebase, email);
      Toastify('error', `Password reset sent to email ${email}`);
    } catch (error) {
      Toastify('error', 'Error sending password reset email');
      console.error('Error sending password reset email', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(authFirebase);
      setAuthUser(null);
    } catch (error) {
      console.error('Error signing out', error);
    }
  };

  const SSOSignIn = (redirectToken: string) => {
    // Skip duplicate SSO sign-ins for the same token (see processedSsoTokens note).
    if (processedSsoTokens.has(redirectToken)) {
      return;
    }
    processedSsoTokens.add(redirectToken);

    signInWithCustomToken(authFirebase, redirectToken)
      .then(async (userCredential: any) => {
        console.log(userCredential.user, 'userCredential.user');
        setAuthUser(userCredential.user);
        setLoading(false);

        localStorage.clear();
        const userResult = await apiClient.get(
          `/user/v1/user/email/${userCredential.user.email}`
        );
        console.log(userResult, 'userResult');

        const userResponse = userResult.data.response;
        if (userResponse.verified) {
          const userData = {
            id: userResponse.id,
            name: userResponse.name,
            email: userCredential.user.email,
            user_hash: userResponse.user_hash,
            brand: userResponse.brand,
            brand_hash: userResponse.brand_hash,
            image: userResponse.image,
            track: userResponse.track_usage,
            team: userResponse.team,
            admin: userResponse.admin_verified,
            weights: userResponse.weights,
            fintech_brands: userResponse.fintech_brands,
            brand_pub_mapping: userResponse.brand_pub_mapping,
            brand_goals: userResponse.brand_goals,
            brand_images: userResponse.brand_images,
            // Omit notifications — can be ~MB+ and is not read from storage; breaks localStorage quota
            taxonomyNotifications: userResponse.taxonomyNotifications,
            mobile: userResponse.mobile,
            meeting_link: userResponse.meeting_link,
            position: userResponse.position,
            is_admin: userResponse.is_admin,
            verified: userResponse?.verified,
          };
          sessionStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('super_admin', JSON.stringify(userData));

          const desc = {
            message: `User with email ${userCredential.user.email} logged in`,
            status: userResult.status,
            method: userResult.config.method,
            url: userResult.config.url,
          };

          const user = JSON.parse(localStorage.getItem('user') as string);
          await actionLogTrack(JSON.stringify(desc), false, {
            id: user.id,
            email: user.email,
            name: user.name,
          });

          setWillNavigate(true);

          Toastify('success', 'Login Successfully');
        } else {
          setWillNavigateForWaiting(true);
        }
      })
      .catch((error) => {
        // Release the token so a genuine retry can proceed after a failure.
        processedSsoTokens.delete(redirectToken);
        Toastify('error', error);
        console.error('Error signing in with token:', error);
      });
  };
  useEffect(() => {
    const unsubscribe = authFirebase.onAuthStateChanged(async (user) => {
      setAuthUser(user);
      setLoading(false);
      
      // Load permissions when auth state changes
      if (user) {
        await refreshPermissions();
      } else {
        setPermissions(null);
        setIsSuperAdmin(false);
        localStorage.removeItem('user_permissions');
        localStorage.removeItem('is_superadmin');
      }
    });

    return unsubscribe;
  }, [refreshPermissions]);

  const contextValue: AuthContextType = {
    authUser,
    loginUserWithPasswordEmail,
    signUpUserWithPasswordEmail,
    createUserAsAdmin,
    loginUserWithGoogleSignIn,
    forgotPassword,
    handleLogout,
    SSOSignIn,
    willnavigate,
    willnavigateForWaiting,
    loading,
    // Permission values
    permissions,
    isSuperAdmin,
    hasPermission,
    canView,
    canEdit,
    canManage,
    refreshPermissions,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
